import { Image } from 'expo-image';
import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Quantity, Text, XStack } from '@fhs/ui';

import { optimizeSanityImageUri } from '../../utils';

import { IMAGE_HEIGHT } from './constants';
import { ListItemUnavailableBadge } from './list-item-unavailable-badge';
import { listStyles } from './shared-styles';
import type { ListItemType } from './types';

type ListItemStepperProps = ListItemType & {
  unavailable?: boolean;
  decrementDisabled?: boolean;
  incrementDisabled?: boolean;
  incrementSoftDisabled?: boolean;
  onIncrementSoftDisabledPress?: () => void;
  value: string;
  onChangeValue: (nextValue: string) => void;
  options: Array<{ label: string; value: string }>;
};

export function ListItemStepper({ minHeightStyle = 'static', ...props }: ListItemStepperProps) {
  const selectedLabel = props.options.find(o => o.value === props.value)?.label ?? '';
  const quantityAccessibilityLabel = [
    props.title,
    selectedLabel ? `Current amount: ${selectedLabel}` : null,
  ]
    .filter(Boolean)
    .join('. ');

  const accessibilityHintParts = [
    typeof props.subtitle === 'string' && props.subtitle.trim() ? props.subtitle.trim() : null,
    'Use the minus and plus buttons to change the amount.',
  ].filter(Boolean);

  const unavailableRowLabel = props.unavailable
    ? [
        props.title,
        typeof props.subtitle === 'string' && props.subtitle.trim() ? props.subtitle.trim() : null,
        'Unavailable',
      ]
        .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
        .join('. ')
    : '';

  return (
    <View
      style={[
        listStyles.item,
        minHeightStyle === 'static' && listStyles.minHeightStyleStatic,
        props.isLastItem && listStyles.lastItem,
      ]}
      accessible={Boolean(unavailableRowLabel)}
      accessibilityLabel={unavailableRowLabel || undefined}
    >
      {props.image?.asset?.uri && (
        <Image
          contentFit="contain"
          style={[
            listStyles.image,
            (props.incrementDisabled || props.unavailable || props.incrementSoftDisabled) &&
              listStyles.imageDisabled,
          ]}
          alt={props.image.altText}
          accessible={!props.unavailable && Boolean(props.image.altText?.trim())}
          accessibilityElementsHidden={
            props.unavailable || !props.image.altText?.trim()
          }
          source={{ uri: optimizeSanityImageUri(props.image.asset.uri, IMAGE_HEIGHT) }}
          placeholder={{ blurhash: props.image.asset.blurHash }}
        />
      )}
      <View
        style={listStyles.titleTextContainer}
        accessible={false}
        importantForAccessibility="no-hide-descendants"
      >
        <View>
          <Text.Ui
            accessible={false}
            importantForAccessibility="no"
            weight={props.titleFontWeight ?? 'semibold'}
            size="md"
            numberOfLines={1}
            style={
              (props.incrementDisabled || props.unavailable || props.incrementSoftDisabled) &&
              listStyles.textDisabled
            }
          >
            {props.title}
          </Text.Ui>
        </View>
        {Boolean(props.subtitle) && (
          <View>
            <Text.Ui
              accessible={false}
              importantForAccessibility="no"
              size="sm"
              numberOfLines={1}
              style={listStyles.subtitle}
            >
              {props.subtitle}
            </Text.Ui>
          </View>
        )}
      </View>
      {props.unavailable ? (
        <ListItemUnavailableBadge />
      ) : (
        <XStack style={listStyles.indicatorWithTextWrapper}>
          {Boolean(props.indicatorText) && (
            <Text.Ui size="sm" numberOfLines={1} style={listStyles.indicatorText}>
              {props.indicatorText}
            </Text.Ui>
          )}
          <Quantity
            options={props.options}
            value={props.value}
            onChangeValue={props.onChangeValue}
            size="sm"
            label={quantityAccessibilityLabel}
            accessibilityHint={accessibilityHintParts.join(' ')}
            decrementAccessibilityLabel={
              props.title
                ? `Less ${props.title}${selectedLabel ? `, currently ${selectedLabel}` : ''}`
                : 'Decrease amount'
            }
            incrementAccessibilityLabel={
              props.title
                ? `More ${props.title}${selectedLabel ? `, currently ${selectedLabel}` : ''}`
                : 'Increase amount'
            }
          >
            <Quantity.Stepper
              style={styles.quantityStepper}
              incrementDisabled={props.incrementDisabled}
              incrementSoftDisabled={props.incrementSoftDisabled}
              onIncrementSoftDisabledPress={props.onIncrementSoftDisabledPress}
            />
          </Quantity>
        </XStack>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  quantityStepper: {
    minWidth: 160,
    justifyContent: 'space-between',
  },
});
